import useQuerySummaries from "@/hooks/data/useQuerySummaries";
import { useQueryColumns } from "@/hooks/gridColumns/useQueryColumns";
import { Button, ModalProps } from "@mui/material";
import { GridEventListener } from "@mui/x-data-grid";
import { CollapsibleTable, EventDefinitionsContext, ICollapsibleTableProps, InstitutionContext, PatientContext, PatientsContext, QueryDialog, useEventById, useFormById, useFormMetadata, useQueryById } from "@ngt/opms";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";

export interface IQueryTableProps extends Omit<ICollapsibleTableProps, "columns" | "rows" | "loading"> {
    onDialogClose?: ModalProps['onClose'];
    preventDefaultDialogClose?: boolean,
    preventDefaultRowClick?: boolean;
}

// export interface IPreviewQueryDialogProps extends DialogProps {
//     fieldName?: string;
//     queryId?: number;
// }

// export const PreviewQueryDialog: FC<IPreviewQueryDialogProps> = ({
//     fieldName,
//     queryId,
//     ...props
// }) => {
//     const {data: query, loading: queryLoading} = useQueryById(queryId);
//     const formMetadata = useFormMetadata(query?.formDefinitionId);
//     const {data: form} = useFormById(formMetadata, query?.formId);
//     const {data: event} = useEventById(form?.eventId);
//     const {data: eventDefinitions} = useContext(EventDefinitionsContext);
//     const {data: patients} = useContext(PatientsContext);
//     const {data: contextPatient} = useContext(PatientContext);
//     const {data: institutions} = useContext(InstitutionsContext);
    
//     const theme = useTheme();

//     const patient = useMemo(() => {
//         if(contextPatient) {
//             return contextPatient;
//         }

//         if(!form) {
//             return undefined;
//         }
//         return patients?.filter(x => x.id == form?.patientId)[0];
//     }, [patients, form, contextPatient]);

//     const institution = useMemo(() => {
//         return institutions?.find(i => i.id == patient?.institutionId);
//     }, [patient?.institutionId, institutions]);

//     const eventDefinition = useMemo(() => {
//         if(event?.eventDefinitionId && eventDefinitions) {
//             return eventDefinitions.find(ed => ed.id == event.eventDefinitionId);
//         }

//         return undefined;
//     }, [eventDefinitions, event?.eventDefinitionId]);

//     const dialogTitle = useMemo(() => {
//         const titleParts: string[] = [];

//         if(patient?.studyNumber) {
//             titleParts.push(patient.studyNumber);
//         }

//         if(eventDefinition?.name) {
//             titleParts.push(eventDefinition.name);
//         }

//         if(formMetadata?.title) {
//             titleParts.push(formMetadata.title);
//         }

//         if(fieldName) {
//             titleParts.push(fieldName);
//         }

//         return titleParts.join(" - ");
//     }, [patient?.studyNumber, eventDefinition?.name, formMetadata?.title, fieldName]);

//     if(queryLoading || !query) {
//         return (
//             <Dialog
//                 maxWidth="md"
//                 fullWidth
//                 {...props}
//             >
//                 <RouteLoading/>
//             </Dialog>
//         )
//     }

//     return (
//         <Dialog
//             maxWidth="md"
//             fullWidth
//             {...props}
//         >
//             <DialogTitle>{dialogTitle}</DialogTitle>
//             <DialogContent
//                 sx={{
//                     margin: theme.spacing(2)
//                 }}
//             >
//                 <QueryGrid 
//                     expanded
//                     query={query}
//                 />
//             </DialogContent>
//             <DialogActions>
//                 <Button
//                     variant={"contained"}
//                     component={Link}
//                     to={`/registration/${institution?.code}/${patient?.studyNumber}/${eventDefinition?.code}/${event?.repeat}/${formMetadata?.formDefinitionCode}/${form?.repeat}`}
//                 >
//                     Go to form
//                 </Button>
//             </DialogActions>
//         </Dialog>
//     );
// }

export const QueryTable: FC<IQueryTableProps> = ({
    onDialogClose,
    preventDefaultDialogClose,
    onRowClick,
    preventDefaultRowClick,
    ...props
}) => {
    const { data: querySummaries, loading: querySummariesLoading, refresh: refreshQuerySummaries } = useQuerySummaries();
    const columns = useQueryColumns(querySummaries);

    
    
    const [selectedQueryId, setSelectedQueryId] = useState<number | undefined>(undefined);
    const [selectedQueryFieldLabel, setSelectedQueryFieldLabel] = useState<string | undefined>(undefined);
    
    const {data: query, loading: queryLoading, refresh: refreshQuery} = useQueryById(selectedQueryId);
    const formMetadata = useFormMetadata(query?.formDefinitionId);
    const {data: form} = useFormById(formMetadata, query?.formId);
    const {data: event} = useEventById(form?.eventId);
    const {data: eventDefinitions} = useContext(EventDefinitionsContext);
    const {data: patients} = useContext(PatientsContext);
    const {data: contextPatient} = useContext(PatientContext);
    const {data: institution} = useContext(InstitutionContext);

    const patient = useMemo(() => {
        if(contextPatient) {
            return contextPatient;
        }

        if(!form) {
            return undefined;
        }
        return patients?.filter(x => x.id == form?.patientId)[0];
    }, [patients, form, contextPatient]);

    const eventDefinition = useMemo(() => {
        if(event?.eventDefinitionId && eventDefinitions) {
            return eventDefinitions.find(ed => ed.id == event.eventDefinitionId);
        }

        return undefined;
    }, [eventDefinitions, event?.eventDefinitionId]);

    const dialogTitle = useMemo(() => {
        const titleParts: string[] = [];

        if(patient?.studyNumber) {
            titleParts.push(patient.studyNumber);
        }

        if(eventDefinition?.name) {
            titleParts.push(eventDefinition.name);
        }

        if(formMetadata?.title) {
            titleParts.push(formMetadata.title);
        }

        if(selectedQueryFieldLabel) {
            titleParts.push(selectedQueryFieldLabel);
        }

        return titleParts.join(" - ");
    }, [patient?.studyNumber, eventDefinition?.name, formMetadata?.title, selectedQueryFieldLabel]);


    const onRowClickCombined = useCallback<GridEventListener<"rowClick">>((params, event, details) => {
        if(!preventDefaultRowClick) {
            setSelectedQueryId(params.row.id);
            setSelectedQueryFieldLabel(params.row.fieldName)
        }

        if(onRowClick) {
            (onRowClick as any)?.(params as any, event, details);
        }
    }, [setSelectedQueryId, setSelectedQueryFieldLabel, onRowClick]);

    const onCloseCombined = useCallback(async (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
        if(onDialogClose) {
            onDialogClose(event, reason);
        }

        if(refreshQuerySummaries) {
            await refreshQuerySummaries();
        }

        if(!preventDefaultDialogClose) {
            setSelectedQueryId(undefined);
            setSelectedQueryFieldLabel(undefined);
        }
    }, [onDialogClose, setSelectedQueryFieldLabel, setSelectedQueryId, refreshQuerySummaries]);

    const onAfterSubmit = useCallback(async () => {
        await refreshQuery();
        await refreshQuerySummaries();
    }, [refreshQuery, refreshQuerySummaries]);

    const additionalActionContent = useMemo(() => [
        () => (
            <Button
                variant={"contained"}
                component={Link}
                to={`/registration/${institution?.code}/${patient?.studyNumber}/${eventDefinition?.code}/${event?.repeat}/${formMetadata?.formDefinitionCode}/${form?.repeat}`}
            >
                Go to form
            </Button>
        )
    ], [institution?.code, patient?.studyNumber, eventDefinition?.code, event?.repeat, formMetadata?.formDefinitionCode, form?.repeat])

    return(
        <>
            {/* <PreviewQueryDialog
                open={!!selectedQueryId}
                fieldName={selectedQueryFieldLabel}
                queryId={selectedQueryId}
                onClose={onCloseCombined}
            /> */}
            <QueryDialog 
                headingText={dialogTitle}
                query={query}
                hideNew
                open={!!selectedQueryId}
                loading={queryLoading}
                onClose={onCloseCombined}
                onAfterSubmit={onAfterSubmit}
                maxWidth={"md"}
                fullWidth
                additionalActionContent={additionalActionContent}
            />
            <CollapsibleTable
                {...props as any}
                rows={querySummaries ?? []}
                columns={columns}
                loading={querySummariesLoading}
                onRowClick={onRowClickCombined}
            />
        </>
    )
}

export default QueryTable;